export default class VideoRecorder {
    idPrefix = '';
    stream = null;
    mediaRecorder = null;
    mimetype = null;
    startTime = 0;
    timerInterval = null;
    openCallback = null;
    sendCallback = null;
    closeCallback = null;
    stopping = false;
    isUserMediaMode = null;
    isAudioOnly = null;

    constructor(idPrefix, openCallback, sendCallback, closeCallback) {
        console.debug('VideoRecorder constructor', idPrefix);

        this.idPrefix = idPrefix;
        this.openCallback = openCallback;
        this.sendCallback = sendCallback;
        this.closeCallback = closeCallback;

        console.debug('VideoRecorder constructor completed', this.idPrefix);
    }

    initUserMedia() {
        console.debug('initUserMedia');

        this.isUserMediaMode = true;
        this.isAudioOnly = false;

        if (navigator.mediaDevices.getUserMedia) {
            console.debug("MediaDevices.getUserMedia() method is supported.");

            const constraints = {
                video: true,
                audio: true
            };
            navigator.mediaDevices.getUserMedia(constraints).then(this.handleOnSuccess, this.handleOnError);
        } else {
            console.debug("MediaDevices.getUserMedia() not supported");
        }

        console.debug('initUserMedia completed');
    }

    initUserMediaAudioOnly() {
        console.debug('initUserMediaAudioOnly');

        this.isUserMediaMode = true;
        this.isAudioOnly = true;

        if (navigator.mediaDevices.getUserMedia) {
            console.debug("MediaDevices.getUserMedia() method is supported.");

            const constraints = {
                video: false,
                audio: true
            };
            navigator.mediaDevices.getUserMedia(constraints).then(this.handleOnSuccess, this.handleOnError);
        } else {
            console.debug("MediaDevices.getUserMedia() not supported");
        }

        console.debug('initUserMedia completed');
    }

    async initDisplayMedia() {
        console.debug('initDisplayMedia');

        this.isUserMediaMode = false;
        this.isAudioOnly = false;

        if (navigator.mediaDevices.getDisplayMedia && navigator.mediaDevices.getUserMedia) {
            console.debug("MediaDevices.getDisplayMedia() and MediaDevices.getUserMedia method are both supported.");

            try {
                // first get the audio user media stream
                const constraints = {
                    video: false,
                    audio: true
                };
                const audioStream = await navigator.mediaDevices.getUserMedia(constraints)

                // then get the display stream
                const options = {
                    video: true,
                    audio: true,
                    systemAudio: "include"
                };
                const displayStream = await navigator.mediaDevices.getDisplayMedia(options);

                // the combine both streams
                const combinedStream = new MediaStream();
                displayStream.getVideoTracks().forEach(t => combinedStream.addTrack(t));
                displayStream.getAudioTracks().forEach(t => combinedStream.addTrack(t));
                audioStream.getAudioTracks().forEach(t => combinedStream.addTrack(t));

                this.handleOnSuccess(combinedStream);
            } catch (error) {
                this.handleOnError(error);
            }
        } else {
            console.debug("MediaDevices.getDisplayMedia() or MediaDevices.getUserMedia not supported");
        }

        console.debug('initDisplayMedia completed');
    }

    handleOnSuccess = newStream => {
        console.debug('handleOnSuccess', newStream);

        this.stream = newStream;

        if (this.isAudioOnly) {
            this.mediaRecorder = new MediaRecorder(this.stream);
        } else {
            this.mediaRecorder = new MediaRecorder(this.stream, {mimeType: 'video/webm'});
        }
        this.mediaRecorder.onstart = this.handleMediaRecorderOnStart;
        this.mediaRecorder.onstop = this.handleMediaRecorderOnStop;
        this.mediaRecorder.ondataavailable = this.handleMediaRecorderOnDataAvailable;

        this.startRecording();

        console.debug("handleOnSuccess completed");
    }

    handleOnError = err => {
        console.debug("The following error occurred: " + err);
    }

    startRecording() {
        console.debug('startRecording');

        // Open web socket
        if (typeof this.openCallback !== 'function') {
            throw new TypeError('Expected a function as openCallback');
        }
        this.openCallback();

        // Start recording
        this.stopping = false;
        this.mediaRecorder.start(5000);

        console.debug("Recorder started", this.mediaRecorder.state);

        console.debug('startRecording completed');
    }

    stopRecording() {
        console.debug('stopRecording');

        if (this.stopping || this.mediaRecorder == null) {
            console.debug('Already stopping or mediaRecorder is null, nothing to stop');
            return;
        }

        // Stop recording
        this.stopping = true
        this.mediaRecorder.stop();

        console.info("Recorder stopped", this.mediaRecorder.state);

        console.debug('stopRecording completed');
    }

    handleMediaRecorderOnStart = e => {
        console.debug("handleMediaRecorderOnStart", e);

        this.showVisualizer();
        this.startTimer();

        this.mimetype = this.mediaRecorder.mimeType;

        console.info("Setting mimetype", this.mimetype);

        console.debug("handleMediaRecorderOnStart completed");
    }

    handleMediaRecorderOnStop = e => {
        console.debug("handleMediaRecorderOnStop", e);

        this.hideVisualizer();
        this.stopTimer();

        VideoRecorder.stopMediaStream(this.stream);

        // Close web socket
        if (typeof this.closeCallback !== 'function') {
            throw new TypeError('Expected a function as closeCallback');
        }
        setTimeout(this.closeCallback, 1000);

        this.stream = null;
        this.mediaRecorder = null;

        console.debug("handleMediaRecorderOnStop completed");
    }

    handleMediaRecorderOnDataAvailable = e => {
        console.debug('handleMediaRecorderOnDataAvailable', e);

        e.data.arrayBuffer().then(this.handleSendMediaBlob);

        console.debug('handleMediaRecorderOnDataAvailable completed');
    }

    handleSendMediaBlob = blobBytesData => {
        console.debug("handleSendMediaBlob");

        // Send data via web socket
        if (typeof this.sendCallback !== 'function') {
            throw new TypeError('Expected a function as sendCallback');
        }
        this.sendCallback(VideoRecorder.arrayBufferToBase64(blobBytesData), this.mimetype, this.stopping);

        console.debug('handleSendMediaBlob completed');
    }

    updateDisplay() {
        const now = new Date().getTime();
        const diff = now - this.startTime;
        let timer = VideoRecorder.elementById(this.idPrefix, 'video-timer');
        if (timer) {
            timer.textContent = VideoRecorder.millisToHuman(diff);
        }
    }

    startTimer() {
        console.debug('startTimer');

        if (this.timerInterval)
            return;

        this.startTime = new Date().getTime();
        this.timerInterval = setInterval(this.updateDisplay.bind(this), 100);

        console.debug('startTimer completed');
    }

    stopTimer() {
        console.debug('stopTimer');

        if (!this.timerInterval)
            return;

        clearInterval(this.timerInterval);
        this.timerInterval = null;
        this.startTime = 0;

        console.debug('stopTimer completed');
    }

    showVisualizer() {
        if (!this.isUserMediaMode) {
            console.debug('Not in user media mode. Skipping video preview');
            return;
        }
        if (this.isAudioOnly) {
            console.debug('In audio only user media mode. Skipping video preview');
            return;
        }

        let preview = VideoRecorder.elementById(this.idPrefix, "video-preview");
        if (preview) {
            preview.srcObject = this.stream;
        }
        let video = VideoRecorder.elementById(this.idPrefix, 'video-container');
        if (video) {
            video.style.display = 'block';
        }
    }

    hideVisualizer() {
        if (!this.isUserMediaMode) {
            console.debug('Not in user media mode. Skipping video preview');
            return;
        }
        if (this.isAudioOnly) {
            console.debug('In audio only user media mode. Skipping video preview');
            return;
        }

        let video = VideoRecorder.elementById(this.idPrefix, 'video-container');
        if (video) {
            video.style.display = 'none';
        }
        let preview = VideoRecorder.elementById(this.idPrefix, "video-preview");
        if (preview) {
            preview.srcObject = null;
        }
    }

    static elementById(idPrefix, id) {
        return document.querySelector('#' + idPrefix + id);
    }

    static millisToHuman(millis) {
        const minutes = Math.floor(millis / 60000);
        const seconds = ((millis % 60000) / 1000).toFixed(0);
        return minutes + ":" + (seconds < 10 ? '0' : '') + seconds;
    }

    static stopMediaStream(stream) {
        console.debug("Stopping media stream", stream);

        stream.getTracks().forEach((track) => {
            if (track.readyState === 'live') {
                track.stop();
            }
        });

        console.debug("Stopped media stream");
    }

    static arrayBufferToBase64(buffer) {
        let binary = '';
        let bytes = new Uint8Array(buffer);
        let len = bytes.byteLength;

        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }
}
